// Borrador local de la historia clínica.
//
// El formulario de historia vive solo en el estado de React: si la página se
// recarga o la sesión redirige a /login mientras el médico escribe, se pierde
// todo. Aquí se guarda una copia en localStorage, una por médico y por cita,
// para ofrecer recuperarla al volver a abrir el formulario.
//
// - Clave: hc-draft:<idDelMédico>:<idDeLaCita>. Con el id del médico en la
//   clave, otro usuario que entre en el mismo navegador no ve el borrador.
// - Caduca a los 7 días; los caducados o corruptos se borran al leerlos.
// - Se borra al guardar la historia y al cerrar sesión (clearAllDrafts).
//
// Todo acceso a localStorage va en try/catch: en modo privado, con la cuota
// llena o con el almacenamiento bloqueado lanza, y un borrador que no se pudo
// guardar no debe romper el formulario.

export const DRAFT_PREFIX = 'hc-draft:';
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const draftKey = (userId, appointmentId) =>
  userId && appointmentId ? `${DRAFT_PREFIX}${userId}:${appointmentId}` : null;

const quitar = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Sin acceso al almacenamiento: no hay nada que borrar.
  }
};

/** Lee y valida un borrador. null si no existe, está corrupto o caducó. */
const leer = (key, now) => {
  let raw = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const draft = JSON.parse(raw);
    const vigente =
      draft &&
      typeof draft.savedAt === 'number' &&
      draft.formData &&
      typeof draft.formData === 'object' &&
      now - draft.savedAt <= DRAFT_TTL_MS;
    if (vigente) return { formData: draft.formData, savedAt: draft.savedAt };
  } catch {
    // JSON inválido: se trata como corrupto.
  }
  quitar(key);
  return null;
};

/** Guarda el borrador. Devuelve false si no se pudo (sin clave o sin almacenamiento). */
export function saveDraft(userId, appointmentId, formData, now = Date.now()) {
  const key = draftKey(userId, appointmentId);
  if (!key) return false;
  try {
    localStorage.setItem(key, JSON.stringify({ formData, savedAt: now }));
    return true;
  } catch {
    return false;
  }
}

/** Devuelve { formData, savedAt } o null. Borra el borrador si caducó o está corrupto. */
export function loadDraft(userId, appointmentId, now = Date.now()) {
  const key = draftKey(userId, appointmentId);
  return key ? leer(key, now) : null;
}

export function clearDraft(userId, appointmentId) {
  const key = draftKey(userId, appointmentId);
  if (key) quitar(key);
}

/** Claves de todos los borradores, recogidas antes de borrar para no saltarse ninguna. */
const clavesDeBorradores = () => {
  const claves = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(DRAFT_PREFIX)) claves.push(key);
    }
  } catch {
    // Sin acceso al almacenamiento.
  }
  return claves;
};

/** Borra todos los borradores del navegador (al cerrar sesión). */
export function clearAllDrafts() {
  clavesDeBorradores().forEach(quitar);
}

/** Borra los borradores caducados o corruptos de cualquier médico y cita. */
export function purgeExpiredDrafts(now = Date.now()) {
  // leer() ya borra lo que no es vigente.
  clavesDeBorradores().forEach((key) => leer(key, now));
}
